import { useState, useRef, useEffect } from 'react';
import { axiosInstance } from '../config/axios';
import { toast } from 'sonner';

/**
 * TaxonomySelect
 * A drop-in replacement for a plain <select> on taxonomy fields.
 * - Renders all `active` values from `options` prop.
 * - Shows "+ Add new…" at the bottom for admin/super_admin users.
 * - On click, reveals an inline input to propose a new value.
 * - Proposed values are sent to POST /api/v1/admin/taxonomy/{typeCode}/propose.
 * - The new value is NOT selectable until approved by a Super Admin.
 *
 * Props:
 *   typeCode     {string}   Taxonomy type code, e.g. 'ball_type'
 *   options      {Array}    Array of {id, key, label} objects (active values only)
 *   value        {string}   Currently selected taxonomy value ID
 *   onChange     {fn}       Called with the selected value ID
 *   placeholder  {string}   Placeholder option text
 *   className    {string}   Extra CSS classes for the <select>
 *   testId       {string}   data-testid for the <select>
 *   canPropose   {boolean}  Whether the current user can propose (admin/super_admin)
 */
export default function TaxonomySelect({
  typeCode,
  options = [],
  value = '',
  onChange,
  placeholder = 'Select...',
  className = '',
  testId,
  canPropose = false,
}) {
  const [showInput, setShowInput]     = useState(false);
  const [newLabel, setNewLabel]       = useState('');
  const [proposing, setProposing]     = useState(false);
  const inputRef                      = useRef(null);

  useEffect(() => {
    if (showInput && inputRef.current) inputRef.current.focus();
  }, [showInput]);

  // Auto-generate a slug key from a human label
  const toKey = (label) =>
    label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

  const handleSelectChange = (e) => {
    if (e.target.value === '__add_new__') {
      setShowInput(true);
      // Reset select visual back to current value
      e.target.value = value || '';
      return;
    }
    setShowInput(false);
    onChange(e.target.value);
  };

  const handlePropose = async () => {
    const label = newLabel.trim();
    if (!label) { toast.error('Please enter a value name'); return; }
    const key = toKey(label);
    if (!key) { toast.error('Invalid value name'); return; }

    setProposing(true);
    try {
      await axiosInstance.post(`/v1/admin/taxonomy/${typeCode}/propose`, {
        key,
        label,
        description: `Proposed by admin: ${label}`,
      });
      toast.success(`"${label}" proposed — awaiting Super Admin approval`);
      setNewLabel('');
      setShowInput(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to propose value';
      if (err?.response?.status === 409) {
        toast.warning(`"${label}" is already pending approval`);
      } else {
        toast.error(msg);
      }
      setShowInput(false);
      setNewLabel('');
    } finally {
      setProposing(false);
    }
  };

  return (
    <div className="relative" data-testid={testId ? `${testId}-wrapper` : undefined}>
      <select
        className={className}
        value={value || ''}
        onChange={handleSelectChange}
        data-testid={testId}
      >
        <option value="">{placeholder}</option>
        {options.map((v) => (
          <option key={v.id} value={v.id}>{v.label}</option>
        ))}
        {canPropose && (
          <option value="__add_new__">+ Add new…</option>
        )}
      </select>

      {/* Inline proposal input */}
      {showInput && (
        <div
          className="absolute z-50 left-0 top-full mt-1 w-full bg-white border border-[#2B3A67] rounded-xl shadow-lg p-3 flex gap-2 items-center"
          data-testid={`${testId}-propose-panel`}
        >
          <input
            ref={inputRef}
            type="text"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handlePropose();
              if (e.key === 'Escape') { setShowInput(false); setNewLabel(''); }
            }}
            placeholder="Type new value name…"
            className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#2B3A67] focus:border-transparent"
            data-testid={`${testId}-propose-input`}
            disabled={proposing}
          />
          <button
            type="button"
            onClick={handlePropose}
            disabled={proposing || !newLabel.trim()}
            className="px-3 py-1.5 bg-[#2B3A67] text-white text-xs font-semibold rounded-lg hover:bg-[#1e2a4a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
            data-testid={`${testId}-propose-submit`}
          >
            {proposing ? 'Proposing…' : 'Propose'}
          </button>
          <button
            type="button"
            onClick={() => { setShowInput(false); setNewLabel(''); }}
            className="text-gray-400 hover:text-gray-600 transition-colors text-sm"
            data-testid={`${testId}-propose-cancel`}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
